import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
  ViewChild
} from "@angular/core";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { FieldType } from "@ngx-formly/core";
import { Store } from "@ngxs/store";
import { ApplicationState, EventAggregator } from "@vp/data-access/application";
import {
  FormSubmitEvent,
  JwtPayload,
  PaymentPayload,
  PaymentTransactionResult,
  TransactionStatusEvent,
  User
} from "@vp/models";
import { CaseContextService } from "@vp/shared/case-context";
import { CybersourceService } from "@vp/shared/cybersource-service";
import { FeatureService } from "@vp/shared/features";
import { FeeService } from "@vp/shared/fee-service";
import { NotificationService } from "@vp/shared/notification-service";
import { filterNullMap } from "@vp/shared/operators";
import { encodeBase64 } from "@vp/shared/utilities";
import { jwtDecode } from "jwt-decode";
import { BehaviorSubject, EMPTY, from, Observable, Subject, timer } from "rxjs";
import { catchError, filter, finalize, switchMap, take, takeUntil, tap } from "rxjs/operators";
declare const Flex: any;
const MONTHS = ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"];
@Component({
  selector: "vp-cybersource-payment",
  templateUrl: "./cybersource-payment.component.html",
  styleUrls: ["./cybersource-payment.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CybersourcePaymentComponent
  extends FieldType
  implements OnInit, AfterViewInit, OnDestroy
{
  private feeService = inject(FeeService);
  private featureService = inject(FeatureService);
  private caseContextService = inject(CaseContextService);
  private cybersourceService = inject(CybersourceService);
  private notificationService = inject(NotificationService);
  private eventAggregator = inject(EventAggregator);

  loggedInUser$: Observable<User | null> = inject(Store).select(ApplicationState.loggedInUser);

  microform: any;

  fee$ = new BehaviorSubject<number>(0);
  yearList: number[] = [];
  months: string[] = MONTHS;
  isRequired = false;
  isPaid$ = new BehaviorSubject<boolean>(false);
  isLoading$ = new BehaviorSubject<boolean>(true);
  cybersourceAdditionalMsg$ = new Observable<string>();
  private disabledSubject = new BehaviorSubject<boolean>(true);
  cardNumberInvalid$ = new BehaviorSubject<boolean>(true);
  cardNumberLength$ = new BehaviorSubject<number>(0);
  securityCodeInvalid$ = new BehaviorSubject<boolean>(true);
  securityCodeTouched$ = new BehaviorSubject<boolean>(false);
  formAttemptedSubmit$ = new BehaviorSubject<boolean>(false);
  expiryInvalid$ = new BehaviorSubject<boolean>(false);
  microformReady$ = new BehaviorSubject<boolean>(false);
  @ViewChild("form") paymentForm!: any;
  private readonly destroyed$ = new Subject<void>();
  private caseId = "";
  firstName: string | undefined;
  lastName: string | undefined;
  address: string | undefined;
  locality: string | undefined;
  administrativeArea: string | undefined;
  country: string | undefined;
  postalCode: string | undefined;

  constructor() {
    super();
    this.populateYearList();
    this.cybersourceAdditionalMsg$ = this.featureService.configurationValue$(
      "caseDashboard",
      "cybersourceAdditionalMsg"
    );
  }

  disabled$ = this.disabledSubject.asObservable();

  ngOnInit(): void {
    this.caseContextService.contextRecordDataValidation
      .pipe(filterNullMap(), takeUntil(this.destroyed$))
      .subscribe(isValid => {
        this.disabledSubject.next(!isValid);
      });

    this.isLoading$.next(true);
    this.caseContextService.caseData$
      .pipe(filterNullMap(), takeUntil(this.destroyed$))
      .subscribe(caseData => {
        this.caseId = caseData.caseId;
        this.isPaid$.next(caseData.paid);
      });
    this.feeService.totalFees$.subscribe(fee => this.fee$.next(fee));

    this.eventAggregator
      .on<FormSubmitEvent>(FormSubmitEvent)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.formAttemptedSubmit$.next(true);
        setTimeout(() => {
          if (this.paymentForm) {
            const ok = this.validateExpiry();
            this.expiryInvalid$.next(!ok);
          }
        }, 200);
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngAfterViewInit(): void {
    this.fee$
      .pipe(
        switchMap(fee => {
          if (fee <= 0 || this.isPaid$.getValue()) {
            this.isPaid$.next(true);
            return EMPTY;
          } else {
            this.isPaid$.next(false);
            // token expires after 15 min, refresh token every 10 min
            return timer(1, 600000).pipe(
              switchMap(_ => this.cybersourceService.getTransientToken()),
              switchMap((tokenResponse: any) => {
                const decoded = jwtDecode(tokenResponse.data) as JwtPayload;
                const contextData = decoded.ctx[0].data;
                const clientLibrary = contextData.clientLibrary;
                const clientLibraryIntegrity = contextData.clientLibraryIntegrity;
                const base64EncodedUrl = encodeBase64(clientLibrary);

                /*Load Library dynamically to adhere to PCI DSS 4.0.1 requirements*/
                return from(this.cybersourceService.getclientLibrary(base64EncodedUrl)).pipe(
                  switchMap((response: any) => {
                    const foundScript = Array.from(
                      document.head.getElementsByTagName("script")
                    ).find(
                      script =>
                        script.integrity === clientLibraryIntegrity &&
                        script.innerHTML === response.clientLibrary
                    );

                    /*Prevent loading script on token refresh if it already exists*/
                    if (!foundScript) {
                      const script = document.createElement("script");
                      script.integrity = clientLibraryIntegrity;
                      script.crossOrigin = "anonymous";
                      script.innerHTML = response.clientLibrary;
                      document.head.appendChild(script);
                    }
                    this.loadMicroform(tokenResponse);
                    return [tokenResponse];
                  }),
                  catchError(error => {
                    console.error("Error loading client library", error);
                    return EMPTY;
                  })
                );
              })
            );
          }
        }),
        takeUntil(this.destroyed$)
      )
      .subscribe();
    if (this.paymentForm && this.paymentForm.valueChanges) {
      this.microformReady$
        .pipe(
          filter(v => v === true),
          take(1),
          switchMap(() =>
            this.paymentForm.valueChanges.pipe(
              takeUntil(this.destroyed$),
              catchError(err => {
                const errorMessage = err instanceof Error ? err.message : String(err);
                this.notificationService.errorMessage(errorMessage);
                return EMPTY;
              })
            )
          ),
          takeUntil(this.destroyed$)
        )
        .subscribe(() => {
          const ok = this.validateExpiry();
          this.expiryInvalid$.next(!ok);
        });
    }
  }

  private isExpiryValid(expMonth: any, expYear: any): boolean {
    const month = Number(expMonth);
    const year = Number(expYear);
    if (!month || !year) return false;
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    if (year > currentYear) return true;
    if (year === currentYear && month >= currentMonth) return true;
    return false;
  }

  private validateExpiry(): boolean {
    const values = this.paymentForm?.value ?? {};
    return this.isExpiryValid(values.expMonth, values.expYear);
  }

  private loadMicroform(tokenResponse: any) {
    const style = {
      input: {
        "font-size": "16px",
        color: "#3A3A3A"
      },
      ":disabled": {
        cursor: "not-allowed"
      },
      valid: {
        color: "green"
      },
      invalid: {
        color: "red"
      }
    };
    const flex = new Flex(tokenResponse.data);
    this.microform = flex.microform({ styles: style });
    const disabled = this.disabledSubject.getValue();
    const cardNumber = this.microform.createField("number", {
      disabled: disabled,
      placeholder: "Enter card number",
      styles: { disabled: { cursor: "not-allowed" } }
    });
    const securityCode = this.microform.createField("securityCode", {
      disabled: disabled,
      placeholder: "***",
      styles: { disabled: { cursor: "not-allowed" } }
    });

    cardNumber.on("change", (data: any) => {
      const container = document.querySelector<HTMLDivElement>("#number-container");
      const brandedCardImg = container?.querySelector<HTMLImageElement>("#branded-card-img");
      const len = data && data.number ? data.number.length : data && data.empty === false ? 1 : 0;
      this.cardNumberLength$.next(len);

      // Treat either `valid === true` or `isValid === true` as valid.
      const isValid = !!(data && (data.valid === true || data.isValid === true));
      this.cardNumberInvalid$.next(!isValid);
      const cardArr = Array.isArray(data && data.card) ? data.card : [];

      if ((cardArr.length === 0 || cardArr.length > 1) && container && brandedCardImg) {
        container.removeChild(brandedCardImg);
      } else if (cardArr.length === 1 && container) {
        const cardBrandedName = cardArr[0].brandedName;
        const cardImage = document.createElement("img");
        cardImage.id = "branded-card-img";

        switch (cardBrandedName) {
          case "Visa":
            if (brandedCardImg) {
              brandedCardImg.className = "visa-card-img";
              brandedCardImg.src = getImagePath(cardBrandedName);
              break;
            }
            cardImage.className = "visa-card-img";
            cardImage.src = getImagePath(cardBrandedName);
            container.appendChild(cardImage);
            break;
          case "MasterCard":
            if (brandedCardImg) {
              brandedCardImg.className = "mastercard-img";
              brandedCardImg.src = getImagePath(cardBrandedName);
              break;
            }
            cardImage.className = "mastercard-img";
            cardImage.src = getImagePath(cardBrandedName);
            container.appendChild(cardImage);
            break;
          case "Discover":
            if (brandedCardImg) {
              brandedCardImg.className = "discover-card-img";
              brandedCardImg.src = getImagePath(cardBrandedName);
              break;
            }
            cardImage.className = "discover-card-img";
            cardImage.src = getImagePath(cardBrandedName);
            container.appendChild(cardImage);
            break;
          default:
            if (brandedCardImg) {
              container.removeChild(brandedCardImg);
            }
            break;
        }
      }
    });
    cardNumber.load("#number-container");

    securityCode.on("change", (data: any) => {
      // mark touched when user interacts
      const hasTyped = data && data.empty === false;
      if (hasTyped) {
        this.securityCodeTouched$.next(true);
      }
      const isValid = !!(data && (data.valid === true || data.isValid === true));
      this.securityCodeInvalid$.next(!isValid);
    });

    securityCode.load("#securityCode-container");
    // Mark microform as ready, refresh expiry validation, and clear loading
    this.isLoading$.next(false);
    this.microformReady$.next(true);
    const ok = this.validateExpiry();
    this.expiryInvalid$.next(!ok);
  }

  onSubmit(form: any) {
    this.isRequired = true;
    this.formAttemptedSubmit$.next(true);
    this.isLoading$.next(true);
    const validExpiry = this.isExpiryValid(form.value.expMonth, form.value.expYear);
    if (!validExpiry) {
      this.expiryInvalid$.next(true);
      this.isLoading$.next(false);
      this.notificationService.errorMessage("Date of expiry is invalid");
      return;
    }
    this.expiryInvalid$.next(false);
    const options = {
      expirationMonth: form.value.expMonth,
      expirationYear: form.value.expYear
    };

    this.microform.createToken(options, (err: any, token: any) => {
      if (err) {
        switch (err.reason) {
          case "CREATE_TOKEN_VALIDATION_FIELDS":
            this.notificationService.errorMessage(err.message);
            break;
          default:
            const msg = err.message || "Payment failed. Please try again.";
            this.notificationService.errorMessage(msg);
            break;
        }
        this.isLoading$.next(false);
      } else {
        const payload = {
          totalAmount: this.fee$.getValue().toFixed(2),
          currency: "USD",
          clientReferenceInformation: `caseId:${this.caseId}`,
          transientToken: token,
          billToInformation: {
            firstName: form.value.firstName,
            lastName: form.value.lastName,
            address: form.value.address,
            locality: form.value.locality,
            administrativeArea: form.value.administrativeArea,
            postalCode: form.value.postalCode,
            country: form.value.country
          }
        } as PaymentPayload;
        this.cybersourceService
          .submitPayment(this.caseId, payload, !this.isPaid$.getValue())
          .pipe(
            finalize(() => {
              this.isLoading$.next(false);
            })
          )
          .subscribe((transactionResult: PaymentTransactionResult) => {
            this.eventAggregator.emit(
              new TransactionStatusEvent(transactionResult),
              "cybersource.submit"
            );
            if (transactionResult.status === "success") {
              this.isPaid$.next(true);
              this.notificationService.successMessage("Payment success");
            } else {
              const msg = transactionResult.message || "Payment failed. Please try again.";
              this.notificationService.errorMessage(msg);
            }
            return EMPTY;
          });
      }
    });
  }

  populatePaymentInfo(matCheckboxChange: MatCheckboxChange) {
    this.loggedInUser$
      .pipe(
        tap(user => {
          if (matCheckboxChange.checked) {
            this.firstName = user?.profile?.firstName;
            this.lastName = user?.profile?.lastName;
            this.address = user?.profile?.address1;
            this.locality = user?.profile?.city;
            this.administrativeArea = user?.profile?.state;
            this.postalCode = user?.profile?.zipCode;
            this.country = user?.profile?.country;
          }
        })
      )
      .subscribe();
  }

  private populateYearList() {
    const year = new Date().getFullYear();
    for (let index = 0; index < 15; index++) {
      this.yearList.push(year + index);
    }
  }
}

function getImagePath(imageName: string): string {
  const basePath = "assets/cybersource/";
  const extension = "_logo.png";
  return `${basePath}${imageName}${extension}`;
}
